import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { Meta } from "@/@types/paginated";
import { ContentContainer, PageLayout, PageTitle, ReportFooter } from "components/layout";
import { Load } from "components/load";
import PageError from "components/page-error";
import { useGetLackTransactionsQuery } from "@/services/lack-transactions";
import { handeOrderBy } from "@/utils/pagination";
import { getPreviousDay } from "@/utils/previousDay";
import { useFilterState } from "../ducks/filter/hooks/filterState";
import { useFilterActions } from "../ducks/filter/hooks/actions";
import { columns } from "./components/table/columns";
import { DataTable } from "./components/table/data-table";
import SelectFilters from "./components/select-filters";
import ReportTabs from "./components/report-tabs";
import StatusGridView from "./components/status-grid";
import { DateRangeHeader } from "./components/status-grid/date-range-header";
import ExportOptions from "./components/export-options";
import { DateRangeType } from "../types";

export const LackOfReconciliation: React.FC = () => {
  const filterState = useFilterState();
  const { filters, activeTab, dateRange: persistedDateRange } = filterState || { filters: {}, activeTab: "lack", dateRange: { from: null, to: null } };
  const { updateActiveTab, updateDateRange } = useFilterActions();

  const dateRange = useMemo<DateRangeType | undefined>(() => {
    if (persistedDateRange?.from && persistedDateRange?.to) {
      return {
        from: parseISO(persistedDateRange.from),
        to: parseISO(persistedDateRange.to),
      };
    }
    return {
      from: getPreviousDay(21),
      to: getPreviousDay(),
    };
  }, [persistedDateRange?.from, persistedDateRange?.to]);

  const handleDateRangeChange = (range: DateRangeType | undefined) => {
    updateDateRange({
      from: range?.from ? range.from.toISOString() : null,
      to: range?.to ? range.to.toISOString() : null,
    });
  };

  const handleTabChange = (tab: "lack" | "status-grid") => {
    updateActiveTab(tab);
  };

  const { order: orderObj, code, responsible, regional_id, register_code } = filters;
  const orderBy = handeOrderBy(orderObj);

  const startDate = dateRange?.from
    ? format(dateRange.from, "yyyy-MM-dd") + "T00:00:00.000Z"
    : undefined;
  const endDate = dateRange?.to
    ? format(dateRange.to, "yyyy-MM-dd") + "T23:59:59.999Z"
    : undefined;

  const {
    data: dataConciliation,
    error,
    isLoading,
    isFetching,
  } = useGetLackTransactionsQuery({
    orderBy: JSON.stringify(orderBy),
    ...(code !== undefined && { code }),
    ...(responsible !== undefined && { responsible }),
    ...(regional_id !== undefined && { regional_id }),
    ...(register_code !== undefined && { register_code }),
    ...(startDate !== undefined && { transactionStartDate: startDate }),
    ...(endDate !== undefined && { transactionEndDate: endDate }),
    search: filters.search,
    perPage: filters?.meta?.perPage || 10,
    meta: {
      page: filters?.meta?.page || 1,
    },
  }, {
    skip: activeTab !== "lack",
  });

  const { data, meta } = dataConciliation || {};

  if (activeTab === "lack" && error) {
    return <PageError status={500} message={"ERRO INTERNO NO SERVIDOR"} />;
  }

  if (activeTab === "lack" && isLoading) {
    return <Load />;
  }

  return (
    <PageLayout>
      <div className="flex-1 w-full flex justify-between items-center">
        <PageTitle
          title="Falta de Conciliação por Loja"
          description="Acompanhe as conciliações pendentes e status por loja"
        />
        <DateRangeHeader
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
        />
      </div>

      <ContentContainer>
        <ReportTabs activeTab={activeTab} onTabChange={handleTabChange} />

        {activeTab === "lack" ? (
          <>
            <div className="mb-4">
              <SelectFilters />
            </div>
            <div className="relative">
              <div className={`transition-all duration-300`}>
                <DataTable
                  data={data as any}
                  meta={meta as Meta}
                  columns={columns}
                  isFetching={isFetching}
                />
              </div>
            </div>
          </>
        ) : (
          <StatusGridView dateRange={dateRange} />
        )}

        <ReportFooter>
          <ExportOptions activeTab={activeTab} dateRange={dateRange} />
        </ReportFooter>
      </ContentContainer>
    </PageLayout>
  );
};
